import { PendingButton } from "@/components/common/PendingButton/PendingButton"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Typography } from "@/components/ui/typography"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  useOrganization,
  type Organization,
} from "@/services/api/organizationApi"
import { Trash2, Upload } from "lucide-react"
import { useRef, useState } from "react"
import { ORG_LOGO_ACCEPT, useOrgLogoActions } from "../hooks/useOrgLogoActions"
import { logoNeedsSetup } from "../setup/setupChecks"
import { SettingsSection } from "./SettingsPage"
import { SettingsLoadError, SettingsSkeleton } from "./SettingsStates"

function LogoForm({ org, shared }: { org: Organization; shared: boolean }) {
  const { translate } = useTranslation()
  const logo = useOrgLogoActions()
  const fileInputRef = useRef<HTMLInputElement>(null)
  // The logo is on every document the org sends, so removing it asks first.
  const [confirmingRemove, setConfirmingRemove] = useState(false)

  async function onRemove() {
    if (await logo.remove()) setConfirmingRemove(false)
  }

  function onFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    // Reset so picking the same file again still fires a change.
    event.target.value = ""
    if (file) void logo.upload(file)
  }

  return (
    <SettingsSection
      title={translate("MC_ORG_SETTINGS_LOGO_LABEL")}
      target="org.logo"
      description={translate(
        shared
          ? "SETTINGS/LOGO/SHARED_DESCRIPTION"
          : "SETTINGS/GENERAL/LOGO_DESCRIPTION",
      )}
      needsSetup={logoNeedsSetup(org)}
    >
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex h-16 w-32 items-center justify-center rounded-md border border-dashed">
          {org.logoUrl ? (
            <img
              src={org.logoUrl}
              alt={translate("MC_ORG_SETTINGS_LOGO_ALT")}
              className="max-h-14 max-w-28 object-contain"
            />
          ) : (
            <Typography as="muted">{translate("MC_TPL_LOGO_NONE")}</Typography>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <PendingButton
              icon={<Upload />}
              pending={logo.isUploading}
              disabled={logo.isRemoving}
              onClick={() => fileInputRef.current?.click()}
            >
              {translate(
                org.logoUrl
                  ? "MC_TPL_LOGO_REPLACE"
                  : "MC_ORG_SETTINGS_LOGO_UPLOAD",
              )}
            </PendingButton>
            {org.logoUrl && (
              <Button
                variant="outline"
                disabled={logo.isUploading || logo.isRemoving}
                onClick={() => setConfirmingRemove(true)}
              >
                <Trash2 />
                {translate("MC_TPL_LOGO_REMOVE")}
              </Button>
            )}
          </div>
          <Typography as="muted">
            {translate("MC_ORG_SETTINGS_LOGO_FORMAT_HINT")}
          </Typography>
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept={ORG_LOGO_ACCEPT}
        onChange={onFileChange}
        className="hidden"
      />
      <AlertDialog
        open={confirmingRemove}
        onOpenChange={(open) => !logo.isRemoving && setConfirmingRemove(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {translate("SETTINGS/GENERAL/REMOVE_LOGO_TITLE")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {translate("SETTINGS/GENERAL/REMOVE_LOGO_DESCRIPTION")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={logo.isRemoving}>
              {translate("MC_MD_EDIT_CANCEL")}
            </AlertDialogCancel>
            <PendingButton
              variant="destructive"
              pending={logo.isRemoving}
              onClick={() => void onRemove()}
            >
              {translate("MC_TPL_LOGO_REMOVE")}
            </PendingButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SettingsSection>
  )
}

/**
 * The organization's logo, upload and remove. One logo for every document,
 * so the same card sits on each page whose documents print it; `shared`
 * says so on the pages other than General.
 */
export function OrgLogoSection({ shared = false }: { shared?: boolean }) {
  const { data: org, isLoading, isFetching, refetch } = useOrganization()
  if (org) return <LogoForm org={org} shared={shared} />
  return (
    <SettingsSection>
      {isLoading ? (
        <SettingsSkeleton rows={1} />
      ) : (
        <SettingsLoadError
          pending={isFetching}
          onRetry={() => void refetch()}
        />
      )}
    </SettingsSection>
  )
}
